// Free Gift Selector Component
class FreeGiftSelector extends HTMLElement {
  constructor() {
    super();
    this.threshold = parseInt(this.dataset.threshold) || 5000;
    this.giftVariantIds = this.dataset.giftVariants ? this.dataset.giftVariants.split(',') : [];
    this.updateDisplay = this.updateDisplay.bind(this);
  }

  connectedCallback() {
    if (this._initialized) return;
    
    this.handleClick = this.handleClick.bind(this);
    this.updateDisplay = this.updateDisplay.bind(this);
    
    this.addEventListener('click', this.handleClick);
    
    this._initialized = true;
    
    document.addEventListener('cart:updated', this.updateDisplay);
    document.addEventListener('cartdrawer:opened', () => {
      setTimeout(() => this.fetchCartAndUpdate(), 50);
    });
    
    setTimeout(() => {
      if (this.isConnected) {
        this.fetchCartAndUpdate();
      }
    }, 50);
  }
  
  handleClick(e) {
    const button = e.target.closest('.free-gift__add-btn');
    if (!button) return;
    
    if (!this._initialized) {
      this.connectedCallback();
    }
    
    if (!button.disabled) {
      e.preventDefault();
      e.stopPropagation();
      this.handleAddGift(e);
    }
  }

  disconnectedCallback() {
    this._initialized = false;
    document.removeEventListener('cart:updated', this.updateDisplay);
    if (this.handleClick) {
      this.removeEventListener('click', this.handleClick);
    }
  }

  fetchCartAndUpdate() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        this.currentCart = cart;
        this.updateProgressAndGifts(cart);
      })
      .catch(console.error);
  }

  updateDisplay(event) {
    setTimeout(() => {
      this.fetchCartAndUpdate();
    }, 100);
  }

  updateProgressAndGifts(cart) {
    if (!cart || !cart.items) {
      return;
    }
    
    let cartTotal = cart.total_price || 0;
    const giftIdsInCart = [];
    
    cart.items.forEach(item => {
      const itemVariantId = item.variant_id ? item.variant_id.toString() : '';
      if (this.giftVariantIds.includes(itemVariantId)) {
        cartTotal -= (item.line_price || 0);
        giftIdsInCart.push(itemVariantId);
      }
    });
    
    const giftCountInCart = giftIdsInCart.length;
    
    const remaining = this.threshold - cartTotal;
    const isUnlocked = cartTotal >= this.threshold;
    
    const progressMessage = this.querySelector('.free-gift__progress-message');
    const unlockedMessage = this.querySelector('.free-gift__unlocked-message');
    const amountElement = this.querySelector('.free-gift__amount');
    
    if (isUnlocked) {
      this.classList.add('is-unlocked');
      if (progressMessage) progressMessage.style.display = 'none';
      if (unlockedMessage) unlockedMessage.style.display = 'block';
    } else {
      this.classList.remove('is-unlocked');
      if (progressMessage) progressMessage.style.display = 'block';
      if (unlockedMessage) unlockedMessage.style.display = 'none';
      if (amountElement) {
        amountElement.textContent = this.formatMoney(remaining);
      }
    }
    
    this.querySelectorAll('.free-gift__card').forEach(card => {
      const variantId = card.dataset.variantId;
      if (!variantId) return;
      
      const addBtn = card.querySelector('.free-gift__add-btn');
      const isInCart = giftIdsInCart.includes(variantId.toString());
      
        if (isInCart) {
        card.classList.add('is-in-cart', 'is-available');
        card.classList.remove('is-locked');
        if (addBtn) {
          addBtn.textContent = this.dataset.addedText || 'Added';
          addBtn.disabled = true;
        }
      } else {
        card.classList.remove('is-in-cart');
        if (addBtn) {
          addBtn.textContent = this.dataset.addText || 'ADD';
          const canAddSample = isUnlocked && giftCountInCart === 0;
          addBtn.disabled = !canAddSample;
        }
        
        const shouldBeAvailable = isUnlocked && giftCountInCart === 0;
        
        if (shouldBeAvailable) {
          card.classList.add('is-available');
          card.classList.remove('is-locked');
        } else {
          card.classList.remove('is-available');
          card.classList.add('is-locked');
        }
      }
    });
  }

  async handleAddGift(event) {
    const button = event.target.closest('.free-gift__add-btn');
    if (!button || button.disabled) return;
    
    const card = button.closest('.free-gift__card');
    if (!card) return;
    
    const variantId = card.dataset.variantId;
    if (!variantId) {
      console.error('Free gift selector: variant ID not found');
      return;
    }
    
    if (this.currentCart) {
      const giftCountInCart = this.currentCart.items.filter(item => 
        this.giftVariantIds.includes(item.variant_id.toString())
      ).length;

      if (giftCountInCart >= 1) {
        return;
      }
    }
    
    button.disabled = true;
    const originalText = button.innerHTML;
    button.innerHTML = '<span class="free-gift__spinner"></span>';
    
    try {
      const miniCart = document.querySelector('mini-cart');
      const cartItems = document.querySelector('cart-items');
      const isCartPage = document.body.classList.contains('template-cart');
      
      let sections = [];
      if (miniCart && !isCartPage) {
        sections = miniCart.getSectionsToRender().map((section) => section.id);
      } else if (cartItems) {
        sections = cartItems.getSectionsToRender().map((section) => section.section);
      } else if (miniCart) {
        sections = miniCart.getSectionsToRender().map((section) => section.id);
      }
      
      const body = JSON.stringify({
        id: parseInt(variantId),
        quantity: 1,
        properties: {
          '_gift': 'true'
        },
        sections: sections,
        sections_url: window.location.pathname
      });
      
      const response = await fetch(`${theme.routes.cart_add_url}`, { 
        ...fetchConfig('javascript'), 
        body 
      });
      
      const parsedState = await response.json();
      
      if (parsedState.status === 422) {
        document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
          detail: {
            errorMessage: parsedState.description
          }
        }));
        button.innerHTML = originalText;
        button.disabled = false;
        return;
      }
      
      if (parsedState.status) {
        button.innerHTML = originalText;
        button.disabled = false;
        return;
      }
      
      const progressBar = document.querySelector('free-sample-progress-bar');
      let preservedProgress = null;
      
      if (progressBar && progressBar.isConnected) {
        preservedProgress = progressBar.currentProgress;
        
        if (preservedProgress === undefined || preservedProgress === null || preservedProgress === 0) {
          let cartTotal = parsedState.total_price || 0;
          
          if (parsedState.items && this.giftVariantIds.length > 0) {
            parsedState.items.forEach(item => {
              const itemVariantId = item.variant_id ? item.variant_id.toString() : '';
              if (this.giftVariantIds.includes(itemVariantId)) {
                cartTotal -= (item.line_price || 0);
              }
            });
          }
          
          if (this.threshold > 0) {
            preservedProgress = Math.min((cartTotal / this.threshold) * 100, 100);
          }
        }
      }
      
      const isCartPage2 = document.body.classList.contains('template-cart');
      if (isCartPage2 && cartItems) {
        cartItems.renderContents(parsedState);
      } else if (miniCart) {
        miniCart.renderContents(parsedState);
      }
      
      const restoreProgressBar = () => {
        const newProgressBar = document.querySelector('free-sample-progress-bar');
        if (newProgressBar && preservedProgress !== null && preservedProgress !== undefined) {
          newProgressBar.currentProgress = preservedProgress;
          newProgressBar.restoreProgress();
          
          const barElement = newProgressBar.querySelector('.free-sample-progress-bar__bar');
          if (barElement) {
            barElement.style.setProperty('--progress', `${preservedProgress}%`);
          }
        }
      };
      
      restoreProgressBar();
      
      requestAnimationFrame(() => {
        restoreProgressBar();
        
        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: { cart: parsedState }
        }));
        
        setTimeout(() => {
          const updatedSelector = document.querySelector('free-gift-selector');
          if (updatedSelector && updatedSelector !== this) {
            updatedSelector.fetchCartAndUpdate();
          } else if (this.isConnected) {
            this.fetchCartAndUpdate();
          }
        }, 50);
      });
      
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
        detail: {
          product: parsedState
        }
      }));
      
    } catch (error) {
      console.error('Error adding gift:', error);
      button.innerHTML = originalText;
      button.disabled = false;
    }
  }

  formatMoney(cents) {
    const currencySymbol = this.dataset.currencySymbol || '€';
    const amount = (cents / 100).toFixed(2).replace('.', ',');
    return `${currencySymbol}${amount}`;
  }
}

if (!customElements.get('free-gift-selector')) {
  customElements.define('free-gift-selector', FreeGiftSelector);
  
  const initExistingElements = () => {
    document.querySelectorAll('free-gift-selector').forEach(el => {
      if (el.isConnected && !el._initialized && typeof el.connectedCallback === 'function') {
        el.connectedCallback();
      }
    });
  };
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initExistingElements);
  } else {
    initExistingElements();
    setTimeout(initExistingElements, 100);
  }
  
  document.addEventListener('cartdrawer:opened', () => {
    setTimeout(initExistingElements, 150);
  });
}